import { inject, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { BuilderContent } from '@builder.io/sdk';
import { ReplaySubject, map } from 'rxjs';
import { LocalizeHelper } from '../helpers/localize.helper';
import { MenuItem } from 'src/app/core/interfaces';
import { BuilderService, GenericLogger } from '@scalefast/ecommerce-core';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class NavMenuService {
  navContent$$: ReplaySubject<MenuItem[]> = new ReplaySubject();
  navContent$ = this.navContent$$.asObservable();
  #logger = inject(GenericLogger);

  constructor(
    private builderService: BuilderService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.getNavContent();
  }

  private getNavContent(): void {
    try {
      this.builderService
        .getContentsByModelName('navigation-menu-config', { includeRefs: true })
        .pipe(map((builderContent: BuilderContent[]) => this.buildMenu(builderContent)))
        .subscribe((builderContent: MenuItem[]) => {
          this.navContent$$.next(builderContent);
        });
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          this.#logger.error({
            msg: 'NavMenuService: Unexpected 404 error requesting Builder navigation-menu-config. Content request failed',
          });
        }
      } else {
        this.#logger.error({ msg: 'NavMenuService: Unexpected error getting navigation menu content', error });
      }
      throw error;
    }
  }

  private buildMenu(builderContent: BuilderContent[]): MenuItem[] {
    let menu: MenuItem[] = [];
    if (!builderContent[0].data?.navigation1) {
      return menu;
    }

    const data = builderContent[0].data?.navigation1;
    if (data) {
      menu = this.getEntry(data, [] as MenuItem[]);
    }

    return menu;
  }

  private getEntry(data: any[], menu: MenuItem[]): MenuItem[] {
    if (data) {
      data.map((d: any) => {
        const entry: MenuItem = {
          dropdownStyle: d.dropdownStyle ?? null,
          title: d.menuEntry ? LocalizeHelper.getLocaleData(d.menuEntry, this.locale) : '',
          hidden: d.visible ?? false,
          linkedPage: this.getLink(d),
          customizeLink: this.getCustomization(d),
          childLinks: this.getEntry(d.childLinks, [] as MenuItem[]),
          image: d.image ?? undefined,
          skipOptimization: d.skipOptimization ?? true,
          hiddenPoh: d.hiddenPoh ?? false,
        };

        menu.push(entry);
      });
    }

    return menu;
  }

  private getLink(page: any): string | undefined {
    let url = null;
    if (page.linkedPage && page.linkedPage.refPage) {
      url = page.linkedPage.refPage.value.data.url;
    } else if (page.linkedPage && page.linkedPage.link) {
      url = page.linkedPage.link;
    }

    return url;
  }

  private getCustomization(page: any): any {
    let customization = {
      underline: false,
      bold: false,
      italic: false,
      shapeColor: null,
      fontColor: null,
      image: null,
    };

    if (page.customizeLink) {
      const { underline, bold, shapeColor, fontColor, italic, image } = page.customizeLink;
      customization.underline = underline ?? customization.underline;
      customization.bold = bold ?? customization.bold;
      customization.italic = italic ?? customization.italic;
      customization.shapeColor = shapeColor ?? customization.shapeColor;
      customization.fontColor = fontColor ?? customization.fontColor;
      customization.image = image ?? customization.image;
    }

    return customization;
  }
}
